import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import {
  Button,
  Input,
  CircularProgress,
  Box,
  Stack,
  ErrorMessage,
  InputLabel,
} from '@/theme/index';
import { schemaLoginForm } from '@/utils/schemaForms/schemaForms';
import { ValidationErrorMessage } from './components';
import { login, UserCredentials, UserData } from './login';
import { CustomWindow } from './useCaptcha';

interface LoginFormInputs extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

export interface LoginFormProps {
  /** Whether to show the captcha. */
  captchaEnabled: boolean;
  /** The captcha type. */
  captcha: string | null;
  /** Whether the captcha is ready to be rendered. */
  captchaReady: boolean;
  /** The captcha sitekey. */
  sitekey: string | null;
  /** Callback to render the captcha. */
  renderCaptcha: (id: string) => void;
  /** Callback fired when the user successfully logs in. */
  onLoginSuccess?: () => void;
  /** Callback fired when the user unsuccessfully logs in. */
  onLoginError?: () => void;
}

const captchaId = 'login-captcha';

/**
 * This component allows users to log in to their account.
 */
export function LoginForm({
  captchaEnabled,
  captcha,
  captchaReady,
  sitekey,
  renderCaptcha,
  onLoginSuccess,
  onLoginError,
}: LoginFormProps) {
  const { handleSubmit, register, formState, getFieldState } = useForm<{
    email: string;
    password: string;
  }>({
    mode: 'onTouched',
    resolver: yupResolver(schemaLoginForm),
  });
  const { logError } = useAppConfigContext();
  const { setSession } = useSession();
  const loginQuery = useMutation<UserData, Error, UserCredentials>({
    mutationFn: login,
    onSuccess: () => {
      setIsAwaitingLoginResponse(false);
      setSession({
        isAuthenticated: true,
      });
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    },
    onError: (error) => {
      if (customWindow.turnstile) {
        customWindow.turnstile.reset();
      }
      setIsAwaitingLoginResponse(false);
      logError('login', error);
      if (onLoginError) {
        onLoginError();
      }
    },
  });

  const customWindow = window as CustomWindow;
  let turnstileForcedRetryCounter = 0;
  const turnstileForcedRetryMaximum = 1;
  const turnstileForcedRetryErrorCodes = [
    'crashed',
    'undefined_error',
    '102***',
    '103***',
    '104***',
    '106***',
    '110600',
    '300***',
    '600***',
  ];

  const [isAwaitingLoginResponse, setIsAwaitingLoginResponse] = useState(false);

  function withinRetryLimit() {
    return turnstileForcedRetryCounter <= turnstileForcedRetryMaximum;
  }

  function isForcedRetryErrorCode(errorCode: string) {
    return turnstileForcedRetryErrorCodes.some((retryErrorCode: string) => {
      if (retryErrorCode.includes('***')) {
        const regexPattern = new RegExp(
          `^${retryErrorCode.replace('***', '')}`
        );
        return regexPattern.test(errorCode);
      }
      return errorCode === retryErrorCode;
    });
  }

  let captchaClassName = '';
  let captchaRenderedExplicitly: boolean;
  switch (captcha) {
    case 'cf-turnstile':
      captchaClassName = 'cf-turnstile';
      captchaRenderedExplicitly = false;
      break;
    case 'hcaptcha':
      captchaClassName = 'h-captcha';
      captchaRenderedExplicitly = true;
      break;
    case 'recaptcha':
      captchaClassName = 'g-recaptcha';
      captchaRenderedExplicitly = true;
      break;
    default:
      captchaClassName = '';
      captchaRenderedExplicitly = false;
  }
  const proxyTokenExists =
    window.env?.RECAPTCHA_DEV_PROXY_TOKEN &&
    window.env?.RECAPTCHA_DEV_PROXY_TOKEN !==
      '%REACT_APP_RECAPTCHA_DEV_PROXY_TOKEN%';
  const shouldRenderCaptchaAboveSubmit =
    captchaEnabled && !captchaRenderedExplicitly && !proxyTokenExists;
  const shouldRenderCaptchaOnSubmit =
    captchaEnabled && captchaRenderedExplicitly && !proxyTokenExists;

  if (!window.handleTurnstileError) {
    window.handleTurnstileError = (err: Error) => {
      if (customWindow.turnstile) {
        turnstileForcedRetryCounter++;
        const errorCode = err.toString();
        if (isForcedRetryErrorCode(errorCode) && withinRetryLimit()) {
          customWindow.turnstile.reset();
        } else {
          // No need to log error as a it is expected behavior for a failed Turnstile check return an error
          const form = document.getElementById('login-form') as HTMLFormElement;
          const captchaResponseField = form.querySelector(
            'input[name="cf-turnstile-response"]'
          ) as HTMLInputElement;
          captchaResponseField.value = errorCode;
        }
      }
    };
  }

  if (!window.logCaptchaError) {
    window.logCaptchaError = (err: Error) => {
      logError('Captcha error', err);
    };
  }

  useEffect(() => {
    window.loginWithCaptcha = (token: string) => {
      const form = document.getElementById('login-form') as HTMLFormElement;
      if (form) {
        const credentials = getCredentials(form);
        if (credentials) {
          loginQuery.mutate({
            userId: credentials.userId,
            password: credentials.password,
            captcha,
            captchaToken: token,
          });
        }
      }
    };
  }, [captcha]);

  const signInText = loginQuery.isLoading ? (
    <CircularProgress sx={{ p: 1 }} color="secondary" />
  ) : (
    'Sign In'
  );

  function handleFormSubmit(data: { email: string; password: string }) {
    if (disabledForm) return;

    const loginData: {
      userId: string;
      password: string;
      [key: string]: string | null; // required for undetermined captcha entries
    } = {
      userId: data.email,
      password: data.password,
    };
    setIsAwaitingLoginResponse(true);
    // NOTE: this submit handler does not get called if hCaptcha is enabled: the hCaptcha script overrides the form submission with custom behavior
    if (shouldRenderCaptchaAboveSubmit && customWindow.turnstile) {
      const form = document.getElementById('login-form') as HTMLFormElement;
      const turnstileResponseField = form.querySelector(
        'input[name="cf-turnstile-response"]'
      ) as HTMLInputElement;
      if (turnstileResponseField && turnstileResponseField.value) {
        loginData.captcha = captcha;
        loginData.captchaToken = turnstileResponseField.value;
        loginQuery.mutate(loginData);
      } else {
        // If the user attempts to submit the form before Turnstile has returned a response, wait 1 second and try again
        setTimeout(() => {
          handleFormSubmit(data);
        }, 1000);
      }
    } else if (!shouldRenderCaptchaOnSubmit) {
      loginQuery.mutate(loginData);
    }
  }

  const disabledForm =
    !captchaReady || isAwaitingLoginResponse || loginQuery.isLoading;

  return (
    <Box
      id="login-form"
      onSubmit={handleSubmit((data) => {
        handleFormSubmit(data);
      })}
      component="form"
    >
      <Stack spacing={2.5}>
        {loginQuery.isError && (
          <ErrorMessage message={loginQuery.error.message} />
        )}
        {loginQuery.isSuccess && <p>Login successful!</p>}
        <Stack spacing={1}>
          <InputLabel htmlFor="email" sx={{ fontWeight: 700 }}>
            Email Address
          </InputLabel>
          <Input
            {...register('email')}
            fullWidth
            id="email"
            type="text"
            name="email"
            placeholder="Email address"
            error={getFieldState('email').invalid}
            data-cy="email_address"
          />
          {formState.errors.email?.message && (
            <ValidationErrorMessage>
              {formState.errors.email?.message}
            </ValidationErrorMessage>
          )}
        </Stack>
        <Stack spacing={1}>
          <InputLabel htmlFor="password" sx={{ fontWeight: 700 }}>
            Password
          </InputLabel>
          <Input
            {...register('password')}
            fullWidth
            id="password"
            type="password"
            name="password"
            placeholder="Password"
            error={getFieldState('password').invalid}
            data-cy="password_field"
          />
          {formState.errors.password?.message && (
            <ValidationErrorMessage>
              {formState.errors.password?.message}
            </ValidationErrorMessage>
          )}
        </Stack>
        <Box textAlign="center">
          {shouldRenderCaptchaAboveSubmit ? (
            <>
              <Box
                className={captchaClassName}
                id={captchaId}
                data-sitekey={sitekey}
                data-error-callback="handleTurnstileError"
                data-size="invisible"
                data-retry="never"
              />
              <Button
                sx={{ width: '172px' }}
                type="submit"
                data-testid="login-with-captcha"
                variant="contained"
                color="success"
                disabled={
                  disabledForm ||
                  !shouldRenderCaptchaAboveSubmit ||
                  !customWindow.turnstile
                }
                data-cy="join_today_btn"
              >
                {disabledForm ||
                !shouldRenderCaptchaAboveSubmit ||
                !customWindow.turnstile ? (
                  <CircularProgress sx={{ p: 1 }} color="secondary" />
                ) : (
                  signInText
                )}
              </Button>
            </>
          ) : shouldRenderCaptchaOnSubmit ? (
            <Button
              type="submit"
              data-testid="login-with-captcha"
              disabled={disabledForm}
              className={captchaClassName}
              data-sitekey={sitekey}
              data-callback="loginWithCaptcha"
              data-error-callback="logCaptchaError"
              variant="contained"
              color="success"
              id={captchaId}
              onClick={
                formState.isValid
                  ? () => {
                      setIsAwaitingLoginResponse(true);
                      renderCaptcha(captchaId);
                    }
                  : undefined
              }
              data-cy="join_today_btn"
              sx={{ width: '172px' }}
            >
              {disabledForm ? (
                <CircularProgress sx={{ p: 1 }} color="secondary" />
              ) : (
                signInText
              )}
            </Button>
          ) : (
            <Button
              sx={{ width: '172px' }}
              type="submit"
              data-testid="login-no-captcha"
              variant="contained"
              color="success"
              disabled={disabledForm}
              data-cy="join_today_btn"
            >
              {disabledForm ? (
                <CircularProgress sx={{ p: 1 }} color="secondary" />
              ) : (
                signInText
              )}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function getCredentials(form: HTMLFormElement | null) {
  if (form) {
    const { email, password }: LoginFormInputs =
      form.elements as LoginFormInputs;
    return {
      userId: email.value,
      password: password.value,
    };
  }
}
